<template>
  <div
    class="simplified-quill-wrapper"
    :class="{ 'is-readonly': disabled, 'is-focused': isFocused }"
  >
    <div
      :id="toolbarId"
      :class="disabled ? 'hide' : 'ql-toolbar ql-snow ql-toolbar-simplified'"
    >
      <div class="d-flex align-items-center justify-content-between w-100">
        <div class="d-flex align-items-center">
          <button class="ql-bold" title="Bold" type="button" />
          <button class="ql-list" value="bullet" title="Bullet list" type="button" />
          <button class="ql-link" title="Link" type="button" />
        </div>
        <div v-if="shouldShowTimestamp" class="d-flex align-items-center">
          <button
            type="button"
            class="btn-timestamp d-flex align-items-center text-secondary border-0 bg-transparent p-0"
            title="Insert timestamp at beginning"
            @click="insertTimestamp"
          >
            <feather-icon icon="ClockIcon" size="14" class="mr-25" />
            <span class="font-small-2 font-weight-bold">Timestamp</span>
          </button>
        </div>
      </div>
    </div>
    <quill-editor
      ref="myQuillEditor"
      v-model="desc"
      :class="disabled ? 'hide-toolbar' : 'quill-custom'"
      :disabled="disabled"
      :options="snowOption"
      @focus="isFocused = true"
      @blur="isFocused = false"
    />
  </div>
</template>

<script>
// eslint-disable-next-line
import 'quill/dist/quill.core.css'
// eslint-disable-next-line
import 'quill/dist/quill.snow.css'
import { quillEditor } from 'vue-quill-editor'
import moment from 'moment-timezone'

let editorCounter = 0

export default {
  name: 'QuillEditor',
  components: {
    quillEditor,
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: 'standard', // 'standard' | 'notes'
    },
    showTimestamp: {
      type: Boolean,
      default: false,
    },
    isReadonly: {
      type: Boolean,
      default: false,
    },
    assign: {
      type: Function,
      default: () => {},
      required: false,
    },
  },
  data() {
    const generatedId = this.id || `quill-toolbar-${++editorCounter}`
    const initialContent = this.value !== null ? this.value : this.description
    return {
      toolbarId: generatedId,
      disabled: this.isReadonly,
      desc: initialContent || '',
      isFocused: false,
      snowOption: {
        modules: {
          toolbar: `#${generatedId}`,
        },
        placeholder:
          this.placeholder ||
          (this.mode === 'notes' ? 'Add notes for this occurrence...' : ''),
      },
    }
  },
  computed: {
    shouldShowTimestamp() {
      return this.showTimestamp || this.mode === 'notes'
    },
  },
  watch: {
    isReadonly(val) {
      this.disabled = val
    },
    value(val) {
      if (val !== this.desc) {
        this.desc = val || ''
      }
    },
    description(val) {
      if (val !== this.desc) {
        this.desc = val || ''
      }
    },
    desc(val) {
      this.$emit('input', val)
      this.$emit('update:description', val)
      this.$emit('assign', val)
    },
  },
  methods: {
    sendDesc() {
      return this.$emit('assign', this.desc)
    },
    insertTimestamp() {
      const quill = this.$refs.myQuillEditor ? this.$refs.myQuillEditor.quill : null
      const tz = this.$tenantTimezone || 'America/Chicago'
      const now = moment().tz(tz)
      const user = this.$store?.state?.user?.currentUser
      const userName = user?.name || ''
      const timestampStr = userName
        ? `${now.format('MMM D, h:mm A')} · ${userName}: `
        : `${now.format('MMM D, h:mm A')}: `

      if (quill) {
        const currentLength = quill.getLength()
        if (currentLength > 1) {
          quill.insertText(0, '\n')
          quill.insertText(0, timestampStr, { bold: true })
          quill.setSelection(timestampStr.length, 0)
          quill.format('bold', false)
        } else {
          quill.insertText(0, timestampStr, { bold: true })
          quill.setSelection(timestampStr.length, 0)
          quill.format('bold', false)
        }
        quill.focus()
      } else {
        this.desc = `<strong>${timestampStr}</strong> ` + (this.desc ? `<br>${this.desc}` : '')
        this.sendDesc()
      }
    },
  },
}
</script>

<style lang="scss">
@import '../../@core/scss/vue/libs/vue-select';

.simplified-quill-wrapper {
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &.is-focused {
    border-color: #7367f0;
    box-shadow: 0 3px 8px 0 rgba(115, 103, 240, 0.1);
  }

  &.is-readonly {
    background-color: #f8f8f8;
    opacity: 0.85;
  }

  .ql-container {
    order: 1;
    border: none !important;
    min-height: 85px;
    font-size: 0.95rem;
    padding: 2px;
  }

  .ql-toolbar-simplified {
    order: 2;
    border: none !important;
    border-top: 1px solid #ebe9f1 !important;
    background-color: #fafafc;
    padding: 4px 8px !important;
    border-radius: 0 !important;

    button:not(.btn-timestamp) {
      width: 28px !important;
      height: 24px !important;
      padding: 3px 5px !important;
      margin-right: 4px;
    }

    .btn-timestamp {
      width: auto !important;
      height: auto !important;
      white-space: nowrap !important;
      display: inline-flex !important;
      align-items: center !important;
      padding: 2px 4px !important;
      margin: 0 !important;
      cursor: pointer;
      color: #6e6b7b;
      transition: color 0.15s ease-in-out;
      &:hover {
        color: #7367f0;
      }
    }
  }

  .hide-toolbar {
    .ql-toolbar {
      display: none;
    }
  }
}
</style>
